import {
  useCallback, useEffect, useMemo, useRef, useState,
  type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type RefObject,
} from 'react'
import type { Rect } from './mapTypes'
import { clampPan, fit, fitHeight, panBy, reveal, zoomAt, type Size, type Viewport } from './viewportMath'

const DRAG_THRESHOLD = 4
const BUTTON_STEP = 1.25

/** SA-448 (spec §4.3): the map's pan and zoom. Fit on first render and on
 * every resize until the person zooms or pans; ctrl/cmd + wheel (a trackpad
 * pinch) zooms about the cursor through a NON-passive native listener —
 * React's own wheel handler is passive, so preventDefault would not stop the
 * browser zooming the page; a pointer drag pans once it has moved
 * DRAG_THRESHOLD, and suppresses the click that ends it; two pointers pinch,
 * and lifting one of them resumes the pan with the other (SA-454). The
 * pointer is captured only once a pan or pinch has actually begun, never on
 * a plain press, because a capturing element is what Chrome retargets the
 * click to — a plain click must still reach the card (SA-459). */
export function useMapViewport(args: {
  contentWidth: number
  contentHeight: number
  viewportWidth: number
  maxHeight: number
  viewportRef: RefObject<HTMLDivElement | null>
}) {
  const content = useMemo<Size>(() => ({ w: args.contentWidth, h: args.contentHeight }), [args.contentWidth, args.contentHeight])
  const height = fitHeight(content, args.viewportWidth, args.maxHeight)
  const view = useMemo<Size>(() => ({ w: args.viewportWidth, h: height }), [args.viewportWidth, height])
  const [vp, setVp] = useState<Viewport>(() => fit(content, view))
  const touched = useRef(false)

  useEffect(() => {
    setVp((prev) => (touched.current ? clampPan(prev, content, view) : fit(content, view)))
  }, [content, view])

  const apply = useCallback((next: (prev: Viewport) => Viewport) => {
    touched.current = true
    setVp(next)
  }, [])
  const zoomIn = useCallback(() => apply((p) => zoomAt(p, BUTTON_STEP, { x: view.w / 2, y: view.h / 2 }, content, view)), [apply, content, view])
  const zoomOut = useCallback(() => apply((p) => zoomAt(p, 1 / BUTTON_STEP, { x: view.w / 2, y: view.h / 2 }, content, view)), [apply, content, view])
  const fitView = useCallback(() => {
    touched.current = false
    setVp(fit(content, view))
  }, [content, view])
  // A reveal follows keyboard focus; it is not the person taking the viewport, so it never marks it touched.
  const revealRect = useCallback((rect: Rect) => setVp((p) => reveal(p, rect, content, view)), [content, view])

  useEffect(() => {
    const el = args.viewportRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      const r = el.getBoundingClientRect()
      apply((p) => zoomAt(p, Math.exp(-e.deltaY * 0.01), { x: e.clientX - r.left, y: e.clientY - r.top }, content, view))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [args.viewportRef, apply, content, view])

  const pointers = useRef(new Map<number, { x: number; y: number }>())
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null)
  const pinch = useRef<number | null>(null)
  const suppressClick = useRef(false)

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return
    // A pointercancel (pointer released outside the window, a browser
    // gesture takeover) sets suppressClick below with no click to clear it —
    // clear it here too so the NEXT genuine click on a card isn't swallowed.
    suppressClick.current = false
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    // A pinch is a gesture from its first second pointer — capture both so
    // touch never needs a click. A single press captures nothing yet (see
    // onPointerMove): that is what lets a plain mouse click reach the card.
    if (pointers.current.size === 2) {
      for (const id of pointers.current.keys()) e.currentTarget.setPointerCapture(id)
    }
    drag.current = pointers.current.size === 1 ? { x: e.clientX, y: e.clientY, moved: false } : null
  }
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const prev = pointers.current.get(e.pointerId)
    if (!prev) return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()]
      const d = Math.hypot(a.x - b.x, a.y - b.y)
      if (pinch.current) {
        const r = e.currentTarget.getBoundingClientRect()
        const factor = d / pinch.current
        apply((p) => zoomAt(p, factor, { x: (a.x + b.x) / 2 - r.left, y: (a.y + b.y) / 2 - r.top }, content, view))
      }
      pinch.current = d
      return
    }
    const dr = drag.current
    if (!dr) return
    if (!dr.moved && Math.hypot(e.clientX - dr.x, e.clientY - dr.y) < DRAG_THRESHOLD) return
    dr.moved = true
    // A pan has begun — the pointer may now leave the element and must
    // still report to it.
    e.currentTarget.setPointerCapture(e.pointerId)
    const dx = e.clientX - prev.x, dy = e.clientY - prev.y
    apply((p) => panBy(p, dx, dy, content, view))
  }
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(e.pointerId)
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
    if (pointers.current.size < 2) pinch.current = null
    if (drag.current?.moved) suppressClick.current = true
    // SA-454: lifting one finger of a pinch resumes panning with the other,
    // from where that finger now is, rather than waiting for a fresh press.
    if (pointers.current.size === 0) drag.current = null
    else if (pointers.current.size === 1) {
      const [remaining] = [...pointers.current.values()]
      drag.current = { x: remaining.x, y: remaining.y, moved: false }
    }
  }
  const onClickCapture = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!suppressClick.current) return
    suppressClick.current = false
    e.stopPropagation()
    e.preventDefault()
  }

  return {
    vp, height, zoomIn, zoomOut, fit: fitView, reveal: revealRect,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onClickCapture },
  }
}
